import { useContext } from 'react';
import {
  Button,
  Form,
  useWindowWidth,
  ProductThemeContext,
  showToast,
  useWindowLocation,
  TextInput,
} from '@prishapolicy/shared/ui';
import { RemixiconReactIconComponentType } from 'remixicon-react';
import CustomerService2FillIcon from 'remixicon-react/CustomerService2FillIcon';
import BarChart2FillIcon from 'remixicon-react/BarChart2FillIcon';
import FileWarningFillIcon from 'remixicon-react/FileWarningFillIcon';
import UmbrellaFillIcon from 'remixicon-react/UmbrellaFillIcon';
import GuideFillIcon from 'remixicon-react/GuideFillIcon';
// import { purchaseFlowFields, PurchaseFlowTextInput } from '@prishapolicy/purchase-flow/forms';
import { object, string } from 'yup';
import { trpcNoAuth } from '@prishapolicy/shared/trpc';
import { regex } from '@prishapolicy/backend-core/masters';

const ChooseUsCard = (props: { heading: string; description: string; icon: RemixiconReactIconComponentType }) => {
  const Icon = props.icon;
  return (
    <div>
      <Icon size={32} className="mb-3 text-black" />
      <h3 className="heading-6">{props.heading}</h3>
      <p className="text-grey-800 pt-1">{props.description}</p>
    </div>
  );
};

const WhyChooseContactForm = () => {
  const currentUrl = useWindowLocation();
  const { isPending: isLoading, mutateAsync } = trpcNoAuth['lead-management']['lead']['create'].useMutation();

  return (
    <div className="bg-primary rounded-l-huge bottom-10 right-0 ml-4 mt-7 px-5 py-10 text-white lg:absolute lg:ml-0 lg:mt-0 lg:w-1/3 lg:p-16">
      <h2 className="lg:heading-2 heading-4"> Still Thinking About It?</h2>
      <p className="mt-3">
        Get on a call with us. We take pride in being associated with you for competitively managing your risks with the
        best insurance covers and delivering a stress-free experience.
      </p>
      <div className="mt-14">
        <Form
          initialValues={{
            personalDetails_fullName: '',
            personalDetails_email: '',
            personalDetails_phone: '',
          }}
          onSubmit={async (values) => {
            await mutateAsync({
              productType: undefined,
              metadata: {
                ...values,
                lead_sourceUrl: currentUrl,
                lead_source: 'WEBSITE',
              },
              leadActivityStatus: undefined,
              leadStatus: undefined,
              activityDate: undefined,
              notes: undefined,
            });
            showToast({
              title: 'Success!',
              description: "We'll get back to you soon.",
              variant: 'success',
            });
          }}
          validationSchema={object().shape({
            personalDetails_fullName: string().required('Full name is required'),
            personalDetails_email: string().email().required('Email is required'),
            personalDetails_phone: string()
              .matches(regex.phone, 'enter 10 digits only')
              .required('Phone number is required'),
          })}
        >
          <div className="grid gap-2">
            <TextInput name="personalDetails_fullName" />
            <TextInput name="personalDetails_email" />
            <TextInput name="personalDetails_phone" />
            <Button text="Get Quote" className="w-full" theme="secondary" disabled={isLoading} />
          </div>
        </Form>
      </div>
    </div>
  );
};

export const WhyChooseUs = () => {
  const productTheme = useContext(ProductThemeContext);

  const chooseUsItems = [
    {
      heading: 'Customer-Centric Culture',
      description:
        'For us, you always come first. Our products and services are designed to find the best coverage for all.',
      icon: CustomerService2FillIcon,
    },
    {
      heading: 'A Decade of Market Leadership',
      description:
        'Our teams expertise and experience makes them the perfect guides in choosing a policy that fits your needs.',
      icon: BarChart2FillIcon,
    },
    {
      heading: 'Reliable and Consistent',
      description:
        'Rest assured that everything will be taken care of without a glitch. From shortlisting to claims processing & delivering promises without a hitch.',
      icon: UmbrellaFillIcon,
    },
    {
      heading: 'Guided Policy Assistance',
      description: 'Our experts are forever ready to assist you through each policy detail with a fine-tooth comb.',
      icon: GuideFillIcon,
    },
    {
      heading: 'Claims Assistance',
      description:
        'Our DNA prompts - Sales is incomplete without timely support. From start to end, we facilitate smooth processing from lodging to settlement.',
      icon: FileWarningFillIcon,
    },
  ];

  const { isLg } = useWindowWidth();
  return (
    <div className={`relative pb-12 lg:pb-0 bg-${productTheme?.primaryShade}`}>
      <div className="container py-16 ">
        <h2 className="heading-2">Why Choose PrishaPolicy</h2>
        <p className="text-grey-800 pt-2 lg:w-1/2">
          5 Reasons why you should choose us to be your policy issuing partner of choice. Allow us to tell you why we
          are one of the fastest growing insurance brokers.
        </p>
        <div className="mt-14 ">
          <div className="grid gap-x-9 gap-y-11 lg:w-1/2 lg:grid-cols-2">
            {chooseUsItems.map((item, idx) => {
              return <ChooseUsCard key={idx} {...item} />;
            })}
          </div>
          {isLg && <WhyChooseContactForm />}
        </div>
      </div>
      {!isLg && <WhyChooseContactForm />}
    </div>
  );
};
